import React, { useState, useMemo, useEffect } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { 
  Search, 
  Filter, 
  ChevronRight, 
  Receipt, 
  ShoppingCart, 
  ArrowUp, 
  ArrowDown, 
  X, 
  Plus,
  FileText,
  RefreshCw
} from 'lucide-react';

// Separate Component Imports
import BillDetailsView from './BillDetailsView';
import AddBillView from './AddBillView';
import SalesReportView from './SalesReportView';
import PurchaseReportView from './PurchaseReportView';
import SampleInvoicesView from './SampleInvoicesView';
import InvoicePreviewView from './InvoicePreviewView';
import PaymentDetailsView from './PaymentDetailsView';
import ExpenseDetailsView from './ExpenseDetailsView';
import AddExpenseView from './AddExpenseView';
import BillReportView from './BillReportView';
import billService from '../../services/billService';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount || 0);
};

const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });
};

const BillType = {
  sale: 'sale',
  purchase: 'purchase',
  expense: 'expense'
};

const BillFilterStatus = {
  all: 'all',
  paid: 'paid',
  unpaid: 'unpaid'
};

const BillSortOption = {
  latestFirst: 'latestFirst',
  oldestFirst: 'oldestFirst',
  highestAmount: 'highestAmount',
  lowestAmount: 'lowestAmount'
};

const BillsHome = ({ setActiveTab, profile }) => {
  const { t } = useLang();
  
  // State
  const [activeReport, setActiveReport] = useState(null);
  const [activeTab, setActiveTabLocal] = useState(BillType.sale);
  const [searchQuery, setSearchQuery] = useState('');
  const [isGstEnabled, setIsGstEnabled] = useState(() => {
    return localStorage.getItem('isGstEnabled') === 'true';
  });

  useEffect(() => {
    localStorage.setItem('isGstEnabled', isGstEnabled);
  }, [isGstEnabled]);

  const [currentFilter, setCurrentFilter] = useState(BillFilterStatus.all);
  const [currentSort, setCurrentSort] = useState(BillSortOption.latestFirst);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showMoreModal, setShowMoreModal] = useState(false);
  
  // Multi-pane state
  const [selectedBill, setSelectedBill] = useState(null);
  const [viewMode, setViewMode] = useState('placeholder'); // 'placeholder', 'details', 'add', 'invoice_preview'
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mock Summary Data (Will be updated by API)
  const [summary, setSummary] = useState({
    monthlySales: 0,
    monthlyPurchases: 0,
    todaysIn: 0,
    todaysOut: 0
  });

  // Real Bills Data from API
  const [bills, setBills] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    const [billsData, summaryData] = await Promise.all([
      billService.fetchBills(),
      billService.fetchSummary()
    ]);
    setBills(billsData);
    if (summaryData) {
      setSummary({
        monthlySales: summaryData.monthly_sales || 0,
        monthlyPurchases: summaryData.monthly_purchases || 0,
        todaysIn: summaryData.todays_in || 0,
        todaysOut: summaryData.todays_out || 0
      });
    }
    setIsLoading(false);
  };

  const nextBillNumber = useMemo(() => {
    if (activeTab === BillType.expense) return "1";
    const tabBills = bills.filter(b => (b.type || '').toLowerCase() === activeTab.toLowerCase());
    if (tabBills.length === 0) return "1";
    
    const numbers = tabBills.map(b => {
      const num = parseInt(b.billNumber);
      return isNaN(num) ? 0 : num;
    });
    return (Math.max(...numbers, 0) + 1).toString();
  }, [bills, activeTab]);

  const filteredBills = useMemo(() => {
    let result = bills.filter(b => (b.type || '').toLowerCase() === activeTab.toLowerCase());
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(b => {
        const customerName = (b.customer?.customerName || '').toLowerCase();
        const billNumber = (b.billNumber || '').toLowerCase();
        if (b.type === BillType.expense) {
          const category = b.expenseCategory?.toLowerCase() ?? '';
          if (category.includes(query)) return true;
        }
        return customerName.includes(query) || billNumber.includes(query);
      });
    }
    if (activeTab !== BillType.expense) {
      if (currentFilter === BillFilterStatus.paid) {
        result = result.filter(b => b.paymentType?.toLowerCase() === 'cash' || b.paymentType?.toLowerCase() === 'online');
      } else if (currentFilter === BillFilterStatus.unpaid) {
        result = result.filter(b => b.paymentType?.toLowerCase() !== 'cash' && b.paymentType?.toLowerCase() !== 'online');
      }
    }
    result.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      switch (currentSort) {
        case BillSortOption.latestFirst: return dateB - dateA;
        case BillSortOption.oldestFirst: return dateA - dateB;
        case BillSortOption.highestAmount: return (parseFloat(b.finalBillAmount || 0)) - (parseFloat(a.finalBillAmount || 0));
        case BillSortOption.lowestAmount: return (parseFloat(a.finalBillAmount || 0)) - (parseFloat(b.finalBillAmount || 0));
        default: return 0;
      }
    });

    // Generate dual entries for Paid Bills (Payment In + Bill)
    const finalDisplayList = [];
    result.forEach(bill => {
      const isPaid = bill.paymentType?.toLowerCase() === 'cash' || bill.paymentType?.toLowerCase() === 'online';
      
      if (isPaid && bill.type !== BillType.expense) {
        // Payment In Entry
        finalDisplayList.push({
          ...bill,
          displayType: 'payment_in',
          uniqueKey: `pay_${bill.id}`
        });
      }
      
      // Bill Entry
      finalDisplayList.push({
        ...bill,
        displayType: 'bill',
        uniqueKey: `bill_${bill.id}`
      });
    });

    return finalDisplayList;
  }, [bills, activeTab, searchQuery, currentFilter, currentSort]);

  const handleBillClick = (bill) => {
    setSelectedBill(bill);
    if (bill.displayType === 'payment_in') {
      setViewMode('payment_details');
    } else {
      setViewMode(bill.type === BillType.expense ? 'expense_details' : 'details');
    }
  };

  const searchHint = activeTab === BillType.sale ? t(LangKeys.bills_search_sales_hint) : activeTab === BillType.purchase ? t(LangKeys.bills_search_purchases_hint) : t(LangKeys.bills_search_expenses_hint);
  
  // If a report is active, show the report view
  if (activeReport === 'sales_report') return <SalesReportView onBack={() => setActiveReport(null)} bills={bills} />;
  if (activeReport === 'purchase_report') return <PurchaseReportView onBack={() => setActiveReport(null)} bills={bills} />;
  if (activeReport === 'bill_report') return <BillReportView onBack={() => setActiveReport(null)} bills={bills} />;
  if (activeReport === 'sample_invoices') return <SampleInvoicesView onBack={() => setActiveReport(null)} />;

  return (
    <div className="print-main-wrapper" style={styles.mainWrapper}>
      {/* LEFT COLUMN: LIST AREA */}
      <div className="no-print" style={{...styles.leftColumn, display: (isMobile && viewMode !== 'placeholder') ? 'none' : 'flex'}}>
        <div style={styles.listHeader}>
          {/* PAGE TITLE */}
          <div style={styles.titleRow}>
             <h2 style={styles.pageTitle}>{t(LangKeys.bills)}</h2>
             <button 
                style={{...styles.refreshBtn, animation: isLoading ? 'spin 1s linear infinite' : 'none'}} 
                onClick={loadData}
                disabled={isLoading}
              >
                <RefreshCw size={18} />
              </button>
          </div>

          {/* 6 CLICKABLE BOXES (DUAL ROW) */}
          <div style={styles.headerGrid}>
             <button style={styles.headerBox} onClick={() => setActiveReport('sales_report')}>
                <div style={styles.boxInfo}>
                  <span style={styles.boxValue}>{formatCurrency(summary.monthlySales)}</span>
                  <span style={styles.boxLabel}>{t(LangKeys.bills_monthly_sales)}</span>
                </div>
                <ChevronRight size={14} color="#94A3B8" />
             </button>
             
             <button style={styles.headerBox} onClick={() => setActiveReport('purchase_report')}>
                <div style={styles.boxInfo}>
                  <span style={styles.boxValue}>{formatCurrency(summary.monthlyPurchases)}</span>
                  <span style={styles.boxLabel}>{t(LangKeys.bills_monthly_purchases)}</span>
                </div>
                <ChevronRight size={14} color="#94A3B8" />
             </button>

             <button style={{...styles.headerBox, justifyContent: 'center', background: '#F8FAFC'}} onClick={() => setActiveReport('bill_report')}>
                <span style={styles.actionText}>{t(LangKeys.bills_view_reports)}</span>
             </button>

             <button style={styles.headerBox}>
                <div style={styles.boxInfo}>
                  <span style={{...styles.boxValue, fontSize: '13px'}}>{formatCurrency(summary.todaysIn)}</span>
                  <span style={styles.boxLabel}>{t(LangKeys.bills_todays_in)}</span>
                </div>
             </button>

             <button style={styles.headerBox}>
                <div style={styles.boxInfo}>
                  <span style={{...styles.boxValue, fontSize: '13px'}}>{formatCurrency(summary.todaysOut)}</span>
                  <span style={styles.boxLabel}>{t(LangKeys.bills_todays_out)}</span>
                </div>
             </button>

             <button 
                style={{...styles.headerBox, justifyContent: 'center', background: '#F8FAFC'}}
                onClick={() => setActiveTab('Cashbook')}
              >
                 <span style={styles.actionText}>{t(LangKeys.bills_cashbook)}</span>
              </button>
          </div>

          {/* TABS */}
          <div style={styles.tabContainer}>
            {[BillType.sale, BillType.purchase, BillType.expense].map(type => (
              <button 
                key={type}
                style={{
                  ...styles.tab,
                  backgroundColor: activeTab === type ? 'white' : 'transparent',
                  color: activeTab === type ? '#1D4ED8' : '#64748B',
                  boxShadow: activeTab === type ? '0 2px 8px rgba(0,0,0,0.05)' : 'none'
                }}
                onClick={() => setActiveTabLocal(type)}
              >
                {type === BillType.sale ? t(LangKeys.bills_tab_sale) : 
                 type === BillType.purchase ? t(LangKeys.bills_tab_purchase) : 
                 t(LangKeys.bills_tab_expense)}
              </button>
            ))}
          </div>

          {/* SEARCH & FILTER */}
          <div style={styles.searchBar}>
            <div style={styles.searchInputWrap}>
              <Search size={18} color="#94A3B8" />
              <input 
                type="text" 
                placeholder={searchHint} 
                style={styles.searchInput}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button 
              style={{...styles.filterBtn, color: (currentFilter !== BillFilterStatus.all || currentSort !== BillSortOption.latestFirst) ? '#1D4ED8' : '#475569', borderColor: (currentFilter !== BillFilterStatus.all || currentSort !== BillSortOption.latestFirst) ? '#1D4ED8' : '#E2E8F0', backgroundColor: (currentFilter !== BillFilterStatus.all || currentSort !== BillSortOption.latestFirst) ? '#EFF6FF' : 'white'}}
              onClick={() => setShowFilterModal(true)}
            >
              <Filter size={18} />
            </button>
          </div>
        </div>

        <div style={styles.scrollArea}>
          {filteredBills.length === 0 ? (
            <div style={styles.emptyStateList}>
               <div style={styles.emptyContent}>
               <h3 style={styles.emptyTitle}>{activeTab === BillType.expense ? t(LangKeys.expense_empty_title) : t(LangKeys.bills_empty_state_title)}</h3>
               <p style={styles.emptySub}>{activeTab === BillType.expense ? t(LangKeys.expense_empty_subtitle) : t(LangKeys.bills_empty_state_subtitle)}</p>
               <button 
                 style={{padding: '8px 16px', borderRadius: '8px', border: '1px solid #1D4ED8', color: '#1D4ED8', background: 'white', fontWeight: '700', cursor: 'pointer', marginTop: '12px'}}
                 onClick={() => setActiveReport('sample_invoices')}
               >
                 {t(LangKeys.bills_see_sample_invoices)}
               </button>
               <div style={styles.emptyIllustration}>{activeTab === BillType.expense ? <ArrowUp size={48} color="#CBD5E1" /> : <Receipt size={48} color="#CBD5E1" />}</div>
               </div>
            </div>
          ) : (
            filteredBills.map((bill) => {
              const isPaid = bill.paymentType?.toLowerCase() === 'cash' || bill.paymentType?.toLowerCase() === 'online';
              const isSelected = selectedBill?.id === bill.id && (
                (bill.displayType === 'payment_in' && viewMode === 'payment_details') ||
                (bill.displayType === 'bill' && (viewMode === 'details' || viewMode === 'expense_details'))
              );
              
              if (bill.displayType === 'payment_in') {
                return (
                  <div key={bill.uniqueKey} style={{...styles.billItem, ...(isSelected ? styles.itemActive : {})}} onClick={() => handleBillClick(bill)}>
                     <div style={styles.itemLeft}>
                       <div style={{...styles.avatar, backgroundColor: '#F0FDF4', color: '#16A34A'}}><ArrowDown size={18} /></div>
                       <div style={styles.itemInfo}>
                          <h4 style={styles.itemName}>{bill.customer?.customerName || 'N/A'}</h4>
                          <span style={styles.itemMeta}>{t(LangKeys.bills_payment_in)} • {formatDate(bill.date)}</span>
                       </div>
                     </div>
                     <div style={styles.itemRight}>
                       <div style={styles.balanceInfo}>
                          <span style={{...styles.balanceAmount, color: '#10B981'}}>{formatCurrency(bill.finalBillAmount)}</span>
                          <span style={styles.balanceStatus}>{bill.paymentType?.toUpperCase()}</span>
                       </div>
                     </div>
                  </div>
                );
              }
              
              return (
                <div key={bill.uniqueKey} style={{...styles.billItem, ...(isSelected ? styles.itemActive : {})}} onClick={() => handleBillClick(bill)}>
                  <div style={styles.itemLeft}>
                    <div style={{
                      ...styles.avatar, 
                      backgroundColor: bill.type === BillType.sale ? '#EFF6FF' : bill.type === BillType.purchase ? '#FFF7ED' : '#FEF2F2',
                      color: bill.type === BillType.sale ? '#1D4ED8' : bill.type === BillType.purchase ? '#C2410C' : '#DC2626'
                    }}>
                      {bill.type === BillType.sale ? <Receipt size={18} /> : bill.type === BillType.purchase ? <ShoppingCart size={18} /> : <ArrowUp size={18} />}
                    </div>
                    <div style={styles.itemInfo}>
                      <h4 style={styles.itemName}>
                        {bill.type === BillType.expense 
                          ? (bill.expenseCategory ? t(bill.expenseCategory) : bill.customer?.customerName || 'N/A') 
                          : (bill.customer?.customerName || 'N/A')}
                      </h4>
                      <span style={styles.itemMeta}>#{bill.billNumber} • {formatDate(bill.date)}</span>
                    </div>
                  </div>
                  <div style={styles.itemRight}>
                    <div style={styles.balanceInfo}>
                      <span style={{...styles.balanceAmount, color: isPaid ? '#10B981' : '#EF4444'}}>{formatCurrency(bill.finalBillAmount)}</span>
                      <div style={{display: 'flex', gap: '4px', alignItems: 'center'}}>
                        <span style={styles.balanceStatus}>{isPaid ? t(LangKeys.bills_status_fully_paid) : t(LangKeys.bills_status_unpaid)}</span>
                        {!isPaid && bill.addedToLedger && <span style={{...styles.balanceStatus, color: '#16A34A'}}>✓</span>}
                      </div>
                    </div>
                    <ChevronRight size={18} color={isSelected ? '#1D4ED8' : '#94A3B8'} />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* BOTTOM ACTION BAR */}
        <div style={styles.bottomBar}>
           <button style={styles.moreBtn} onClick={() => setShowMoreModal(true)}>{t(LangKeys.bills_more_button)}</button>
           <button 
             style={{...styles.addBtn, backgroundColor: activeTab === BillType.purchase ? '#16A34A' : '#1D4ED8'}} 
             onClick={() => { setSelectedBill(null); setViewMode('add'); }}
           >
             <Plus size={20} />
             <span>{activeTab === BillType.sale ? t(LangKeys.bills_add_sale) : activeTab === BillType.purchase ? t(LangKeys.bills_add_purchase) : t(LangKeys.bills_add_expense)}</span>
           </button>
        </div>
      </div>

      {/* RIGHT COLUMN: DETAIL AREA */}
      <div className="print-right-column" style={styles.rightColumn}>
        {viewMode === 'placeholder' ? (
          <div style={styles.placeholderContainer}>
            <div style={styles.placeholderIconBox}><FileText size={64} color="#E2E8F0" /></div>
            <h3 style={styles.placeholderTitle}>{t(LangKeys.select_entry)}</h3>
            <p style={styles.placeholderSub}>{t(LangKeys.entry_details_appear)}</p>
          </div>
        ) : viewMode === 'details' ? (
          <div style={styles.ledgerCardWrap}>
             <BillDetailsView 
               bill={selectedBill} 
               onBack={() => setViewMode('placeholder')} 
               onViewPdf={() => setViewMode('invoice_preview')} 
               onEdit={(b) => { setSelectedBill(b); setViewMode('add'); }}
               onDeleted={() => { setViewMode('placeholder'); loadData(); }}
             />
          </div>
        ) : viewMode === 'payment_details' ? (
          <div style={styles.ledgerCardWrap}>
             <PaymentDetailsView bill={selectedBill} onBack={() => setViewMode('placeholder')} />
          </div>
        ) : viewMode === 'expense_details' ? (
          <div style={styles.ledgerCardWrap}>
             <ExpenseDetailsView 
               bill={selectedBill} 
               onBack={() => setViewMode('placeholder')} 
               onEdit={(b) => { setSelectedBill(b); setViewMode('add'); }}
               onDeleted={() => { setViewMode('placeholder'); loadData(); }}
             />
          </div>
        ) : viewMode === 'invoice_preview' ? (
          <div className="print-ledger-card-wrap" style={styles.ledgerCardWrap}>
             <InvoicePreviewView 
                bill={selectedBill} 
                businessProfile={profile} 
                onBack={() => { setViewMode('placeholder'); loadData(); }} 
                onEdit={() => setViewMode('add')}
                onDelete={async () => {
                  const success = await billService.deleteBill(selectedBill.id);
                  if (success) {
                    setViewMode('placeholder');
                    loadData();
                  } else {
                    alert("Failed to delete bill");
                  }
                }}
                onCreateNew={() => { setSelectedBill(null); setViewMode('add'); }}
             />
          </div>
        ) : viewMode === 'add' ? (
          <div style={styles.ledgerCardWrap}>
             {activeTab === 'expense' ? (
                <AddExpenseView 
                  initialExpense={selectedBill}
                  onBack={() => setViewMode('placeholder')} 
                  onSaved={() => { setViewMode('placeholder'); loadData(); }} 
                />
             ) : (
                <AddBillView 
                  type={selectedBill ? selectedBill.type : activeTab} 
                  profile={profile}
                  isGst={selectedBill ? (selectedBill.isGst || selectedBill.items?.some(i => (i.taxAmount || 0) > 0 || (i.gstRate && i.gstRate !== '0%'))) : isGstEnabled} 
                  initialBill={selectedBill}
                  initialBillNumber={nextBillNumber}
                  onBack={() => setViewMode('placeholder')} 
                  onSaved={async (savedBill) => { 
                    // Set current bill immediately so preview isn't empty
                    setSelectedBill(savedBill);
                    setViewMode('invoice_preview');
                    
                    // Then fetch full details in background for completeness
                    try {
                      const fullBill = await billService.getBillDetails(savedBill.id);
                      if (fullBill) {
                        setSelectedBill(prev => {
                          const merged = { ...fullBill };
                          if (prev) {
                            merged.partyAddress = merged.partyAddress || prev.partyAddress;
                            if (merged.customer && prev.customer) {
                              merged.customer = {
                                ...prev.customer,
                                ...merged.customer,
                                address: merged.customer.address || prev.customer.address
                              };
                            }
                          }
                          return merged;
                        });
                      }
                    } catch (e) { console.error("Error refetching bill:", e); }
                    
                    loadData(); 
                  }}
                />
             )}
          </div>
        ) : (
          <div style={styles.placeholderContainer}>
             {/* Fallback */}
          </div>
        )}
      </div>

      {/* MODALS */}
      {showFilterModal && (
        <div style={styles.modalOverlay} onClick={() => setShowFilterModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>{t(LangKeys.filter_and_sort)}</h3>
              <X size={24} style={{cursor: 'pointer'}} onClick={() => setShowFilterModal(false)} />
            </div>
            <div style={styles.modalSection}>
               <span style={styles.sectionTitle}>{t(LangKeys.filter_by_status)}</span>
               <div style={styles.optionGroup}>
                  {[BillFilterStatus.all, BillFilterStatus.paid, BillFilterStatus.unpaid].map(val => (
                    <button key={val} style={{...styles.option, ...(currentFilter === val ? styles.optionActive : {})}} onClick={() => setCurrentFilter(val)}>{val === 'all' ? t(LangKeys.filter_all_bills) : val === 'paid' ? t(LangKeys.filter_paid_bills) : t(LangKeys.filter_unpaid_bills)}</button>
                  ))}
               </div>
            </div>
            <div style={styles.modalSection}>
               <span style={styles.sectionTitle}>{t(LangKeys.sort_by)}</span>
               <div style={styles.optionGroup}>
                  {[BillSortOption.latestFirst, BillSortOption.oldestFirst, BillSortOption.highestAmount, BillSortOption.lowestAmount].map(val => (
                    <button key={val} style={{...styles.option, ...(currentSort === val ? styles.optionActive : {})}} onClick={() => setCurrentSort(val)}>{t(LangKeys['sort_' + val.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)] || val)}</button>
                  ))}
               </div>
            </div>
            <button style={styles.applyBtn} onClick={() => setShowFilterModal(false)}>{t(LangKeys.apply_filters)}</button>
          </div>
        </div>
      )}

      {showMoreModal && (
        <div style={styles.modalOverlay} onClick={() => setShowMoreModal(false)}>
           <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                 <h3 style={styles.modalTitle}>{t(LangKeys.more)}</h3>
                 <X size={24} style={{cursor: 'pointer'}} onClick={() => setShowMoreModal(false)} />
              </div>
              <div style={styles.switchRow}>
                 <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <Receipt size={20} color="#1D4ED8" />
                    <span style={{fontWeight: '700'}}>{t(LangKeys.bills_start_creating_gst)}</span>
                 </div>
                 <div style={{...styles.switch, backgroundColor: isGstEnabled ? '#1D4ED8' : '#CBD5E1'}} onClick={() => setIsGstEnabled(!isGstEnabled)}>
                    <div style={{...styles.switchHandle, transform: isGstEnabled ? 'translateX(20px)' : 'translateX(0)'}} />
                 </div>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  mainWrapper: { display: 'flex', height: 'calc(100vh - 80px)', background: '#F8FAFC', overflow: 'hidden' },
  leftColumn: { width: '420px', borderRight: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', background: '#FFFFFF', flexShrink: 0, position: 'relative' },
  rightColumn: { flex: 1, background: '#F8FAFC', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', padding: '24px' },
  listHeader: { padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px', background: '#FFFFFF', borderBottom: '1px solid #F1F5F9' },
  titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' },
  pageTitle: { fontSize: '20px', fontWeight: '800', color: '#1E293B', margin: 0 },
  headerGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' },
  headerBox: { background: '#FFFFFF', border: '1px solid #F1F5F9', borderRadius: '12px', padding: '10px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', textAlign: 'left', outline: 'none' },
  boxInfo: { display: 'flex', flexDirection: 'column', gap: '2px' },
  boxValue: { fontSize: '14px', fontWeight: '800', color: '#0F172A' },
  boxLabel: { fontSize: '9px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase' },
  actionText: { fontSize: '11px', fontWeight: '800', color: '#1D4ED8', textAlign: 'center' },
  tabContainer: { display: 'flex', gap: '4px', background: '#F1F5F9', padding: '4px', borderRadius: '12px' },
  tab: { flex: 1, padding: '10px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s' },
  searchBar: { display: 'flex', gap: '10px' },
  searchInputWrap: { flex: 1, display: 'flex', alignItems: 'center', gap: '10px', background: '#F8FAFC', padding: '0 16px', borderRadius: '12px', border: '1px solid #E2E8F0', height: '44px' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '14px', background: 'transparent' },
  filterBtn: { width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px', border: '1px solid #E2E8F0', cursor: 'pointer' },
  scrollArea: { flex: 1, overflowY: 'auto', paddingBottom: '100px' },
  billItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: '#FFFFFF', cursor: 'pointer', transition: 'all 0.2s' },
  itemActive: { background: '#EFF6FF' },
  itemLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  avatar: { width: '44px', height: '44px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  itemInfo: { display: 'flex', flexDirection: 'column', gap: '2px' },
  itemName: { fontSize: '15px', fontWeight: '700', color: '#0F172A', margin: 0 },
  itemMeta: { fontSize: '12px', color: '#94A3B8', fontWeight: '600' },
  itemRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  balanceInfo: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' },
  balanceAmount: { fontSize: '15px', fontWeight: '800' },
  balanceStatus: { fontSize: '11px', fontWeight: '700', color: '#64748B' },
  bottomBar: { position: 'absolute', bottom: '24px', left: '20px', right: '20px', display: 'flex', gap: '12px', zIndex: 10 },
  moreBtn: { width: '100px', padding: '14px', borderRadius: '12px', border: '1px solid #E2E8F0', background: 'white', color: '#475569', fontWeight: '800', cursor: 'pointer' },
  addBtn: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '14px', borderRadius: '12px', border: 'none', color: '#FFFFFF', fontSize: '15px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 10px 25px rgba(29, 78, 216, 0.2)' },
  placeholderContainer: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: '#F1F5F9' },
  placeholderIconBox: { width: '120px', height: '120px', background: '#FFFFFF', borderRadius: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' },
  placeholderTitle: { fontSize: '22px', fontWeight: '800', color: '#1E293B', margin: '0 0 12px 0' },
  placeholderSub: { fontSize: '14px', color: '#94A3B8', textAlign: 'center', maxWidth: '250px' },
  refreshBtn: { border: 'none', background: '#F1F5F9', color: '#64748B', width: '36px', height: '36px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: '0.2s' },
  ledgerCardWrap: { flex: 1, background: '#FFFFFF', borderRadius: '24px', border: '1px solid #E2E8F0', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalContent: { background: '#FFFFFF', borderRadius: '24px', width: '100%', maxWidth: '400px', padding: '24px' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  modalTitle: { fontSize: '18px', fontWeight: '800', margin: 0 },
  modalSection: { marginBottom: '20px' },
  sectionTitle: { display: 'block', fontSize: '12px', fontWeight: '700', color: '#64748B', marginBottom: '10px', textTransform: 'uppercase' },
  optionGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  option: { padding: '12px', borderRadius: '10px', border: '1px solid #E2E8F0', textAlign: 'left', background: '#F8FAFC', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  optionActive: { background: '#EFF6FF', borderColor: '#1D4ED8', color: '#1D4ED8' },
  applyBtn: { width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: '#1D4ED8', color: 'white', fontWeight: '800', cursor: 'pointer' },
  switchRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: '#F8FAFC', borderRadius: '16px' },
  switch: { width: '44px', height: '24px', borderRadius: '12px', padding: '2px', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: '0.3s' },
  switchHandle: { width: '20px', height: '20px', background: 'white', borderRadius: '50%', transition: '0.3s' },
  emptyStateList: { padding: '40px 20px', textAlign: 'center' },
  emptyContent: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' },
  emptyTitle: { fontSize: '16px', fontWeight: '700', color: '#475569' },
  emptySub: { fontSize: '13px', color: '#94A3B8' },
  emptyIllustration: { marginTop: '20px' }
};

export default BillsHome;
